import type { ExportFormat } from '@reports/domain/types';

/**
 * Value object representing an export configuration for a report
 *
 * Encapsulates export configuration with validation and business rules.
 * Immutable by design to ensure data integrity.
 *
 * @property {name} - Display name for the export (translation key)
 * @property {endpoint} - API endpoint to fetch the export data
 * @property {slugs} - Array of slug identifiers for the export sections
 * @property {format} - Export file format (currently only Excel)
 *
 * @example
 * const exportConfig: ExportConfig = {
 *   name: 'reportSales:export.name',
 *   endpoint: 'api/v1/sales/export',
 *   slugs: ['summary', 'details'],
 *   format: ExportFormats.EXCEL
 * };
 */
export interface ExportConfig {
  readonly name: string;
  readonly endpoint: string;
  readonly slugs: ReadonlyArray<string>;
  readonly format: ExportFormat;
}

type CreateExportConfigProps = {
  name: string;
  endpoint: string;
  slugs: string[];
  format: ExportFormat;
};

/**
 * Validates export configuration properties
 *
 * @param props - Export config properties to validate
 * @throws {Error} When validation fails
 */
const validateExportConfig = (props: CreateExportConfigProps): void => {
  if (!props.name?.trim()) {
    throw new Error('Export config name is required');
  }

  if (!props.endpoint?.trim()) {
    throw new Error('Export config endpoint is required');
  }

  if (!Array.isArray(props.slugs) || props.slugs.length === 0) {
    throw new Error('Export config must have at least one slug');
  }

  props.slugs.forEach((slug) => {
    if (!slug?.trim()) {
      throw new Error('Export config slug cannot be empty');
    }
  });

  if (!props.format) {
    throw new Error('Export config format is required');
  }
};

/**
 * Factory function to create an export configuration
 *
 * Validates all properties and returns an immutable ExportConfig.
 *
 * @param props - The properties to create the export config
 * @returns {ExportConfig} The created export configuration
 *
 * @example
 * const exportConfig = createExportConfig({
 *   name: 'reportSales:export.name',
 *   endpoint: 'api/v1/sales/export',
 *   slugs: ['summary', 'details'],
 *   format: ExportFormats.EXCEL
 * });
 */
export const createExportConfig = (props: CreateExportConfigProps): ExportConfig => {
  validateExportConfig(props);

  return Object.freeze({
    name: props.name,
    endpoint: props.endpoint,
    slugs: props.slugs,
    format: props.format,
  });
};
